import React, { useEffect, useState } from "react";
import { Nav } from "react-bootstrap";
import { ShimmerContentBlock, ShimmerButton } from "react-shimmer-effects";
import "./CashForDeed.css";

import {
  getAllCFDProperties,
  getCfdPropertiesBySearchUri,
  getHistoryCFDProperties,
  getSavedCFDProperties,
} from "@/api/cfd";
import SidebarMenu from "@/components/common/header/dashboard/SidebarMenu";
import Header from "@/components/common/header/DefaultHeader";
import MobileMenu from "@/components/common/header/MobileMenu";
import CfdEmptyCard from "./CfdEmptyCard";
import CfdCardItem from "./cfdComponents/CfdCardItem";
import CfdPagination from "./cfdComponents/cfdPagination/CfdPagination";
import CfdPropertyFilters from "./cfdComponents/cfdFilters/cfdPropertyFilters";
import CfdHeaderSection from "./cfdComponents/cfdHeaders/CfdHeaderSection";
import CfdTimerSection from "./cfdComponents/cfdTimer/CfdTimerSection";
import { useSelector } from "react-redux";
import MetaComponent from "@/components/common/MetaComponent";
import { trackButtonClick, trackPageMount } from "@/utils/analytics";

const metadata = {
  title: "Cash for Deed Opportunities",
};

const CashForDeed = () => {
  const savedCount = useSelector((state) => state.savedPreference);

  const [filters, setFilters] = useState({
    propertyType: [],
    occupancy: [],
    timeFrame: "",
  });
  const [activeTab, setActiveTab] = useState("all");
  const [cfdProperties, setCfdProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchUri, setSearchUri] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [cfdProgramEndDateTime, setCfdProgramEndDateTime] = useState(null);
  const [totalFetchedCount, setTotalFetchedCount] = useState(0);

  useEffect(() => {
    const fetchPropertiesByTab = async () => {
      let response;
      try {
        setLoading(true);
        if (activeTab === "all") {
          if (searchQuery && searchUri) {
            response = await getCfdPropertiesBySearchUri({
              filters,
              searchURI: searchUri,
              pageNumber: currentPage,
            });
            if (response?.data && response?.data?.srpTupples?.length > 0) {
              const searchProperties = response.data.srpTupples;
              setCfdProperties(searchProperties);
              setTotalCount(response.data.totalResultCount || 0);

              // Update total fetched count
              if (currentPage === 1) {
                setTotalFetchedCount(searchProperties.length);
              } else {
                setTotalFetchedCount(
                  (prevCount) => prevCount + searchProperties.length
                );
              }
            } else {
              setCfdProperties([]);
              setTotalCount(0);
              setTotalFetchedCount(0);
            }
          } else {
            response = await getAllCFDProperties({
              pageNumber: currentPage,
              filters,
            });
            if (response?.status === "SUCCESS") {
              const newProperties = response?.srpTuppleList || [];
              setCfdProperties(newProperties);
              setCfdProgramEndDateTime(response?.cfdProgramEndDateTime || null);
              setTotalCount(response?.totalTransactions || 0);

              // Update total fetched count
              if (currentPage === 1) {
                setTotalFetchedCount(newProperties.length);
              } else {
                setTotalFetchedCount(
                  (prevCount) => prevCount + newProperties.length
                );
              }
            } else {
              setCfdProperties([]);
              setTotalCount(0);
              setTotalFetchedCount(0);
            }
          }
        } else if (activeTab === "saved") {
          response = await getSavedCFDProperties(currentPage);
          if (response?.responseStatus === "SUCCESS") {
            const newProperties = response.propertyWatchResponseList || [];
            setCfdProperties(newProperties);
            setCfdProgramEndDateTime(response?.cfdProgramEndDateTime || null);
            setTotalCount(response.watchCount || 0);

            // Update total fetched count
            if (currentPage === 1) {
              setTotalFetchedCount(newProperties.length);
            } else {
              setTotalFetchedCount(
                (prevCount) => prevCount + newProperties.length
              );
            }
          } else {
            currentPage > 1 && setCurrentPage(currentPage - 1);
            setCfdProperties([]);
            setTotalCount(0);
            setTotalFetchedCount(0);
          }
        } else if (activeTab === "history") {
          response = await getHistoryCFDProperties(
            currentPage,
            filters.timeFrame || "placedOn"
          );
          if (response?.status === "SUCCESS") {
            const newProperties = response.srpTuppleList || [];
            setCfdProperties(newProperties);
            setCfdProgramEndDateTime(response.cfdProgramEndDateTime || null);
            setTotalCount(response?.totalTransactions || 0);

            // Update total fetched count
            if (currentPage === 1) {
              setTotalFetchedCount(newProperties.length);
            } else {
              setTotalFetchedCount(
                (prevCount) => prevCount + newProperties.length
              );
            }
          } else {
            setCfdProperties([]);
            setTotalCount(0);
            setTotalFetchedCount(0);
          }
        }

        cfdProperties.length === 0 &&
          trackPageMount({
            pageName: "cfd_dashboard_render",
            eventCategory: "my-shopper",
            eventSource: "on cfd card render",
          });
      } catch (error) {
        console.error("Error fetching listings:", error);
        setCfdProperties([]);
        setTotalCount(0);
        setTotalFetchedCount(0);
      } finally {
        setLoading(false);
      }
    };

    // Debounced API call to reduce excessive requests
    const timeoutId = setTimeout(() => {
      fetchPropertiesByTab();
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId); // Cleanup function
  }, [
    filters,
    currentPage,
    activeTab,
    searchUri,
    setSearchUri,
    savedCount.savedCount,
  ]);

  const handleFilterChange = (filterType, value, tabName) => {
    setActiveTab(tabName || "all");
    setCurrentPage(1);
    setTotalFetchedCount(0);
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterType]: value,
    }));
  };

  const handleTabChange = (tabName) => {
    setLoading(true);
    setSearchQuery("");
    setActiveTab(tabName || "all");
    setCurrentPage(1);
    setTotalCount(0);
    setTotalFetchedCount(0);
    setFilters({
      propertyType: [],
      occupancy: [],
      timeFrame: "",
    });

    trackButtonClick({
      eventName: "cfd_tab_click",
      eventCategory: "CFD dashboard",
      eventSource: "on tab click",
      eventParams: {
        tab:
          tabName == "all"
            ? "all_listings"
            : tabName == "saved"
            ? "saved"
            : "offer_history",
      },
    });
  };

  return (
    <>
      <Header />
      <MobileMenu />
      <MetaComponent meta={metadata} />

      <div className="dashboard_sidebar_menu">
        <div
          className="offcanvas offcanvas-dashboard offcanvas-start"
          tabIndex="-1"
          id="DashboardOffcanvasMenu"
          data-bs-scroll="true"
        >
          <SidebarMenu />
        </div>
      </div>

      {/* Main Section */}
      <section
        className="our-dashbord dashbord pb50 cfd-section-margin"
        style={{ marginTop: "-20px" }}
      >
        {/* OFFER TIMER */}
        {cfdProgramEndDateTime && (
          <CfdTimerSection timer={cfdProgramEndDateTime} />
        )}

        {/* TITLE & GLOBAL SEARCH SECTION */}
        <CfdHeaderSection
          activeTab={activeTab}
          setSearchUri={setSearchUri}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          resetState={handleFilterChange}
        />

        {/* TABS & FILTERS */}
        <div className="real-estate-tabs-container">
          <Nav
            className="real-estate-tabs"
            variant="tabs"
            activeKey={activeTab}
            onSelect={handleTabChange}
          >
            <Nav.Item className="cfd-nav-menu-items">
              <Nav.Link eventKey="all">All Listings</Nav.Link>
            </Nav.Item>
            <Nav.Item className="cfd-nav-menu-items">
              <Nav.Link eventKey="saved">Saved</Nav.Link>
            </Nav.Item>
            <Nav.Item className="cfd-nav-menu-items last">
              <Nav.Link eventKey="history">Offer History</Nav.Link>
            </Nav.Item>
          </Nav>
          {/* filter for property type, occupancy and new properties */}
          <CfdPropertyFilters
            filters={filters}
            activeTab={activeTab}
            onFilterChange={handleFilterChange}
          />
        </div>

        {/* LISTING CARDS WITH SHIMMER EFFECT */}
        <div className="px-2">
          {loading ? (
            <>
              {[...Array(3)].map((_, index) => (
                <div key={index} className="mb-4 cfd-shimmer-wrapper">
                  <ShimmerContentBlock
                    title
                    text
                    cta
                    thumbnailWidth={200}
                    thumbnailHeight={180}
                  />
                </div>
              ))}
            </>
          ) : cfdProperties.length > 0 ? (
            cfdProperties.map((item) => (
              <CfdCardItem
                key={item.propertyId}
                item={item}
                activeTab={activeTab}
                onRefresh={handleTabChange}
              />
            ))
          ) : (
            <CfdEmptyCard activeTab={activeTab} />
          )}
        </div>

        {/* PAGINATION WITH SHIMMER EFFECT */}
        <div
          style={{
            display: "flex",
            margin: "auto",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loading ? (
            <ShimmerButton size="md" />
          ) : (
            cfdProperties.length > 0 && (
              // totalFetchedCount <= totalCount && (
              <CfdPagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / 10)}
                setCurrentPage={setCurrentPage}
              />
            )
          )}
        </div>
      </section>
    </>
  );
};

export default CashForDeed;
